import React, {useState, useEffect, useMemo, useContext, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {Dictionary} from '@reduxjs/toolkit';
import classNames from 'classnames';
import {connect} from 'react-redux';
import _ from 'lodash';

import style from './ValidateTable.module.scss';

import {
  State,
  AssetField,
  ReduxState,
  SelectOption
} from '../../interface';
import useSearch from '../../hook/useSearch';
import { useBulkEditData, IBulkEditData } from '../../hook/useBulkEditData';
import InitTable, {Query} from '../Table/Table';
import {InputType} from '../Table/components/Editor/Editor';
import Modal from '../Modal/Modal';
import ModalContext from '../../context/ModalContext';
import filterAssetsQuery from '../../utils/filterAssetsQuery';
import FieldSelect from '../FieldSelect/FieldSelect';
import FieldToggleCheckbox from '../FieldToggleCheckbox/FieldToggleCheckbox';

import { Props, TModalConfirmation, EModalConfirmationStep } from './types';

const {Table, Column, TopBar} = InitTable<AssetField>();

function ValidateTable(props: Readonly<Props>) {
  const navigate = useNavigate();
  const {filteredData} = useSearch();
  const {
    columnMappings,
    assets,
    dataList,
    standardColumns,
    additionColumn,
    checkedColumns,
    mappedKeys,
    type,
    url,
    onDeleteItem,
    onUpdateAssets,
    states,
    fetching,
    updating,
  } = props;
  const {state, dispatch} = useContext(ModalContext);

  const [selected, setSelected] = useState<AssetField[]>([]);
  const [query, setQuery] = useState<Dictionary<Query>>({});
  //Filter
  const [options, setOptions] = useState<Dictionary<unknown[]>>({});
  const [deleteItem, setDeleteItem] = useState<AssetField>();
  const [bulkEditButtonStatus, setBulkEditButtonStatus] = useState<
  'disabled' | 'enabled'
  >('disabled');

  const isSelected = selected.length > 0;
  const {
    isLoading,
    companiesOptions,
    legalEntitiesOptions,
    propertiesOptions,
    assessorAccountsOptions,
    statesOptions,
    bulkEditData,
    toggleBulkEditData,
    onCancelBulkEditData,
    onToggleSwitches,
    onSelectValue,
  } = useBulkEditData({ isSelected, states });
  const isDataLoading = Object.values(isLoading).every(Boolean);

  const modalConfirmationBody = (step: EModalConfirmationStep) => {
    const assetsLength = selected.length > 1 ? `${selected.length} assets` : 'this asset';
    return `Are you sure you want to ${step} ${assetsLength}?`;
  };
  const [modalConfirmation, setModalConfirmation] = useState<TModalConfirmation>({
    body: '',
    step: EModalConfirmationStep.DELETE
  });

  const onConfirmDelete = () => {
    setModalConfirmation({
      body: modalConfirmationBody(EModalConfirmationStep.DELETE),
      step: EModalConfirmationStep.DELETE,
    });
    dispatch({type: 'OPEN', href: 'confirm'});
  };

  const columns = useMemo(
    () => [...additionColumn, ...standardColumns],
    [standardColumns, additionColumn],
  );
  const defaultColumns = useMemo(
    () => new Set(checkedColumns),
    [checkedColumns],
  );

  useEffect(() => {
    setOptions((current) => ({
      ...current,
      status: _.compact(_.uniq(_.map(dataList, 'status'))),
      company: _.filter(
        _.uniqBy(
          _.map(dataList, (item) => ({
            name: _.get(item, 'companyName'),
            number: _.get(item, 'companyNumber'),
          })),
          'name',
        ),
        (item) => !_.isEmpty(item.name) || !_.isEmpty(item.number),
      ),
      legalEntity: _.filter(
        _.uniqBy(
          _.map(dataList, (item) => ({
            name: _.get(item, 'legalEntityName'),
            number: _.get(item, 'legalEntityNumber'),
          })),
          'name',
        ),
        (item) => !_.isEmpty(item.name) || !_.isEmpty(item.number),
      ),
      state: _.intersectionBy(
        states,
        _.map(dataList, (item) => ({id: _.get(item, 'stateId')})),
        'id',
      ),
      property: _.filter(
        _.uniqBy(
          _.map(dataList, (item) => ({
            name: _.get(item, 'propertyName'),
            number: _.get(item, 'propertyNumber'),
          })),
          'number',
        ),
        (item) => !_.isEmpty(item.name) || !_.isEmpty(item.number),
      ),
      class: _.compact(_.uniq(_.map(dataList, 'class'))),
      assessor: _.compact(_.uniq(_.map(dataList, 'assessor'))),
    }));
  }, [dataList]);

  const result = _.orderBy(
    filteredData(
      filterAssetsQuery(dataList, query[type]?.filter),
      _.lowerCase(query[type]?.search),
    ),
    [_.keys(query[type]?.order)[0] ? _.keys(query[type]?.order)[0] : 'id'],
    [_.values(query[type]?.order)[0] ? _.values(query[type]?.order)[0] : 'asc'],
  );

  const chunk = _.chunk(result, query[type]?.pagination?.pageSize)[
    (query[type]?.pagination?.page as number) - 1
  ];

  const confirm = (e: React.MouseEvent) => {
    if (modalConfirmation.step === EModalConfirmationStep.DELETE) {
      if (deleteItem?.id) {
        onDeleteItem(_.reject(dataList, ['id', deleteItem?.id]));
        setDeleteItem(undefined);
      } else {
        onDeleteItem(
          _.reject(dataList, (item) =>
            _.includes(_.map(selected, 'id'), item.id),
          ),
        );
      }
    } else if (modalConfirmation.step === EModalConfirmationStep.UPDATE) {
      onUpdateAssetsConfirm();
      onCancelBulkEditData();
    }
    dispatch({type: 'CLOSE'});
    setSelected([]);
    e.stopPropagation();
  };

  const formatSelectedData = (key: keyof IBulkEditData, type: 'items' | 'columns') => {
    switch (key) {
      case 'state': {
        const { abbreviation, id } = bulkEditData.state;
        return type === 'items' ? {
          state: abbreviation,
          stateId: id,
        } : {
          'State': abbreviation,
        }
      }
      case 'assessorAccount': {
        const { name, number, id, assessor } = bulkEditData.assessorAccount;
        return type === 'items' ? {
          assessor: assessor?.name || '',
          assessorAccount: { name, number },
          assessorAccountId: id,
          assessorAccountNumber: number,
        } : {
          'Assessor': assessor?.name || '',
          'Assessor Account Number': number,
        }
      }
      default: {
        let propertyStateId = {};
        if (key === 'property' && type === 'items') {
          propertyStateId = { propertyStateId: bulkEditData.property.address?.stateId as unknown as string };
        }
        return type === 'items' ? {
          [`${key}Id`]: bulkEditData[key].id,
          [`${key}Name`]: bulkEditData[key].name,
          [`${key}Number`]: bulkEditData[key].number,
          ...propertyStateId,
        } : {
          [`${_.startCase(key)} Name`]: bulkEditData[key].name,
          [`${_.startCase(key)} Number`]: bulkEditData[key].number,
        };
      }
    }
  };

  const onConfirmUpdate = () => {
    setModalConfirmation({
      body: modalConfirmationBody(EModalConfirmationStep.UPDATE),
      step: EModalConfirmationStep.UPDATE,
    });
    dispatch({type: 'OPEN', href: 'confirm'});
  };

  const onUpdateAssetsConfirm = () => {
    const updatedSelectedItems = (assetsList: any[], type: 'items' | 'columns') =>
      assetsList.map((asset) => {
        const selectedAsset = selected.find((selectedAsset) => selectedAsset.id === asset.id);

        if (selectedAsset) {
          return {
            ...asset,
            ...(bulkEditData.company?.id
              ? formatSelectedData('company', type)
              : {}),
            ...(bulkEditData.legalEntity?.id
              ? formatSelectedData('legalEntity', type)
              : {}),
            ...(bulkEditData.state?.id
              ? formatSelectedData('state', type)
              : {}),
            ...(bulkEditData.property?.id
              ? formatSelectedData('property', type)
              : {}),
            ...(bulkEditData.assessorAccount?.id
              ? formatSelectedData('assessorAccount', type)
              : {}),
            ...(type === 'items'
              ? {
                isAddition: toggleBulkEditData.addition,
                disposed: toggleBulkEditData.disposed
              } : {}),
          };
        }
        return asset;
      });
    const newUpdatedColumnMappings = updatedSelectedItems(columnMappings, 'columns');
    const newUpdatedSelectedItems = updatedSelectedItems(assets, 'items');

    onUpdateAssets({
      columnMappings: newUpdatedColumnMappings,
      items: newUpdatedSelectedItems,
    });
  };

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => ref?.current?.blur(), [state.isOpen]);

  useEffect(() => {
    const fieldSelectValues = Object.values(bulkEditData);
    const isAnyFieldSelectFilled = fieldSelectValues.some(
      (field) => field?.id
  );
    const isAdditionOrDisposed = toggleBulkEditData.addition || toggleBulkEditData.disposed;

    if (isAnyFieldSelectFilled || isAdditionOrDisposed) {
      setBulkEditButtonStatus('enabled');
    } else {
      setBulkEditButtonStatus('disabled');
    }
  }, [bulkEditData, toggleBulkEditData]);

  return (
    <div className={style['validate-assets']}>
      <Table
        id={type}
        rows={chunk || []}
        paginate
        selectable={{selected, onChange: setSelected}}
        searchable='Search assets...'
        onQueryChanged={(x) => setQuery({...query, [type]: x})}
        totalRows={result.length}
        dynamicColumns
        loading={fetching}
      >
        <TopBar>
          {selected.length > 0 && (
            <div className={style['selected-items-action']}>
              <FieldSelect
                classnames={style['field-select']}
                labelText='Company Name & Number'
                options={companiesOptions}
                disabled={isLoading.company}
                selectId={bulkEditData.company?.id}
                onSelect={(option) => onSelectValue('company', option as SelectOption)}
              />
              <FieldSelect
                classnames={style['field-select']}
                labelText='Legal Entity Name & Number'
                options={legalEntitiesOptions}
                disabled={isLoading.legalEntity}
                selectId={bulkEditData.legalEntity?.id}
                onSelect={(option) => onSelectValue('legalEntity', option as SelectOption)}
              />
              <FieldSelect
                classnames={style['field-select']}
                labelText='State'
                options={statesOptions}
                selectId={bulkEditData.state?.id}
                onSelect={(option) => onSelectValue('state', option as SelectOption)}
              />
              <FieldSelect
                classnames={style['field-select']}
                labelText='Property Number'
                options={propertiesOptions}
                disabled={isLoading.property}
                selectId={bulkEditData.property?.id}
                onSelect={(option) => onSelectValue('property', option as SelectOption)}
              />
              <FieldSelect
                classnames={style['field-select']}
                labelText='Assessor Account Number'
                options={assessorAccountsOptions}
                disabled={isLoading.assessorAccount}
                selectId={bulkEditData.assessorAccount?.id}
                onSelect={(option) => onSelectValue('assessorAccount', option as SelectOption)}
              />
              <FieldToggleCheckbox
                label="Addition"
                data-testid='switch-button-addition'
                value={toggleBulkEditData.addition}
                onChange={() => onToggleSwitches('addition')}
              />
              <FieldToggleCheckbox
                label="Disposed"
                data-testid='switch-button-disposed'
                value={toggleBulkEditData.disposed}
                onChange={() => onToggleSwitches('disposed')}
              />
              <button
                className='primary'
                onClick={onConfirmUpdate}
                disabled={bulkEditButtonStatus !== 'enabled' || isDataLoading}
              >
                {updating
                  ? 'Sending...'
                  : `Update Assets (${selected.length})`
                }
              </button>
              <button
                className='secondary'
                onClick={onConfirmDelete}
                disabled={updating || isDataLoading}
              >
                Delete Assets ({selected.length})
              </button>
              <button
                className='secondary'
                onClick={onCancelBulkEditData}
                disabled={bulkEditButtonStatus !== 'enabled' || isDataLoading}
              >
                Cancel
              </button>
            </div>
          )}
        </TopBar>
        {columns.map((col, idx) => (
          <Column
            key={`col-${idx}-${col}`}
            hidden={!defaultColumns.has(col)}
            id={_.get(mappedKeys, col)}
            label={col}
            sortable={true}
          >
            {(asset: AssetField) => {
              const value = _.get(asset, _.get(mappedKeys, col), null);
              const isBoolean = typeof value === 'boolean';
              const booleanValueDisplay = value ? 'Yes' : 'No';

              return col === 'Import status' ? (
                <span
                  className={classNames(
                    'badge-status',
                    _.kebabCase(value as string),
                  )}
                  // Error Message
                  title={asset.errors && asset.errors.length > 0 ? asset.errors.map(error => (error as unknown as any).message).join(', ') : ''}
                >
                  {value}
                </span>
              ) : (
                <span className={style['noWrap']}>
                  {isBoolean ? booleanValueDisplay : value}
                </span>
              )
            }}
          </Column>
        ))}
        <Column id='actions' label='Actions'>
          {(property: AssetField) => (
            <div className='operate'>
              <i
                role='button'
                className='edit'
                tabIndex={0}
                title='Edit'
                onClick={() =>
                  navigate(`../${url}?itemId=${property.id}`, {
                    state: location.pathname,
                  })
                }
              ></i>
              <i
                role='button'
                tabIndex={0}
                className='delete'
                title='Delete'
                onClick={() => {
                  onConfirmDelete();
                  setDeleteItem(property);
                }}
              ></i>
            </div>
          )}
        </Column>
        {/* Status filter */}
        <Column
          id='status'
          hidden='always'
          label={['Status', 'Import Status']}
          filterable
          editor={{
            type: InputType.Checkbox,
            options: options['status'] ? options['status'] : [],
          }}
        />
        {/* Company name filter */}
        <Column
          id='companyName'
          hidden='always'
          label={['Company', 'Select one or more companies']}
          filterable
          editor={{
            type: InputType.Select,
            options: options['company'] ? options['company'] : [],
            getLabel: (x) => `${_.get(x, 'name')} | ${_.get(x, 'number')}`,
            getValue: (x) => _.get(x, 'name'),
            multi: true,
          }}
        />
        {/* Legal Entity filter */}
        <Column
          id='legalEntityName'
          hidden='always'
          label={['Legal Entity', 'Select one or more legal entities']}
          filterable
          editor={{
            type: InputType.Select,
            options: options['legalEntity'] ? options['legalEntity'] : [],
            getLabel: (x) => `${_.get(x, 'name')} | ${_.get(x, 'number')}`,
            getValue: (x) => _.get(x, 'name'),
            multi: true,
          }}
        />
        <Column
          id='propertyNumber'
          hidden='always'
          label={['Property', 'Select one or more properties']}
          filterable
          editor={{
            type: InputType.Select,
            options: options['property'] ? options['property'] : [],
            getLabel: (x) => `${_.get(x, 'name')} | ${_.get(x, 'number')}`,
            getValue: (x) => _.get(x, 'number'),
            multi: true,
          }}
        />
        <Column
          id='stateId'
          hidden='always'
          label={['State', 'Select one or more states']}
          filterable
          editor={{
            type: InputType.Select,
            options: options['state'] ? options['state'] : [],
            getLabel: (x) => (x as State).abbreviation,
            getValue: (x) => (x as State).id,
            multi: true,
          }}
        />
        <Column
          id='assessor'
          hidden='always'
          label={['Assessor', 'Select one or more assessor names']}
          filterable
          editor={{
            type: InputType.Select,
            options: options['assessor'] ? options['assessor'] : [],
            multi: true,
          }}
        />

        <Column
          id='class'
          hidden='always'
          label={['Asset Class', 'Select one or more asset classes']}
          filterable
          editor={{
            type: InputType.Select,
            options: options['class'] ? options['class'] : [],
            multi: true,
          }}
        />
      </Table>
      <Modal
        title='Confirmation'
        body={<p>{modalConfirmation.body}</p>}
        isOpen={state.isOpen && state.href === 'confirm'}
        footer={
          <div className='buttons'>
            <button
              className='primary'
              onClick={(e) => confirm(e)}
              ref={ref}
            >
              Confirm
            </button>
            <button
              className='secondary'
              onClick={() => dispatch({type: 'CLOSE'})}
            >
              Cancel
            </button>
          </div>
        }
      />
    </div>
  );
}

const mapStateToProps = (state: ReduxState) => ({
  states: state.states,
});

export default connect(mapStateToProps)(ValidateTable);
